/**
 * CoreProvider - Foundation provider bundle
 * 
 * Provides ActrProvider and all Actr-dependent providers in the correct order.
 * This ensures consistent foundation across all OpCentral apps.
 * 
 * CHANGE HISTORY:
 * - 2025-11-15 danielbeer: Initial creation - standardized provider orchestration
 */

import React, { ReactNode, useMemo } from 'react';
import { ActrProvider } from '@opcentral/actr';
import { CommandrProvider } from '@opcentral/commandr';
import { NotifyrProvider } from '@opcentral/notifyr';
import { SkinnrProvider } from '@opcentral/skinnr';
import { BacktrackrProvider } from '@opcentral/backtrackr';
import { ContextrManager } from '@opcentral/contextr';
import { Aggregatr } from '@opcentral/aggregatr';

export interface CoreProviderProps {
  children: ReactNode;
  
  // Actr configuration
  actrConfig?: {
    namespace?: string;
    storage?: any;
    contextr?: any;
    aggregatr?: any;
    [key: string]: any;
  };
  
  // Feature toggles
  enableCommandr?: boolean;
  enableNotifications?: boolean;
  enableTheming?: boolean;
  enableHistory?: boolean;
  
  // Skinnr configuration
  skinnrTheme?: string | 'auto';
  skinnrNamespace?: string;
  
  // Notifyr configuration
  notifyrOptions?: any;
}

/**
 * CoreProvider - Provides foundation layer
 * 
 * Hierarchy:
 * ActrProvider (foundation)
 *   └─ CommandrProvider (keyboard shortcuts)
 *       └─ NotifyrProvider (notifications)
 *           └─ SkinnrProvider (theming)
 *               └─ BacktrackrProvider (undo/redo)
 * 
 * All of these providers require or benefit from ActrProvider.
 */
export function CoreProvider({
  children,
  actrConfig = {},
  enableCommandr = true,
  enableNotifications = true,
  enableTheming = true,
  enableHistory = true,
  skinnrTheme = 'auto',
  skinnrNamespace = 'app',
  notifyrOptions = {},
}: CoreProviderProps) {
  
  // Auto-inject Contextr and Aggregatr (Integration Manager pattern)
  const enhancedActrConfig = useMemo(() => {
    const config = { ...actrConfig };
    
    // Auto-inject ContextrManager if not provided
    if (!config.contextr) {
      config.contextr = new ContextrManager({ autoTrack: false });
    }
    
    // Auto-inject Aggregatr if not provided
    if (!config.aggregatr) {
      config.aggregatr = new Aggregatr();
    }
    
    return config;
  }, [actrConfig]);
  
  return (
    <ActrProvider options={enhancedActrConfig}>
      {enableCommandr ? (
        <CommandrProvider>
          {enableNotifications ? (
            <NotifyrProvider options={notifyrOptions}>
              {enableTheming ? (
                <SkinnrProvider
                  initialTheme={skinnrTheme}
                  namespace={skinnrNamespace}
                >
                  {enableHistory ? (
                    <BacktrackrProvider>
                      {children}
                    </BacktrackrProvider>
                  ) : children}
                </SkinnrProvider>
              ) : enableHistory ? (
                <BacktrackrProvider>
                  {children}
                </BacktrackrProvider>
              ) : children}
            </NotifyrProvider>
          ) : enableTheming ? (
            <SkinnrProvider
              initialTheme={skinnrTheme}
              namespace={skinnrNamespace}
            >
              {enableHistory ? (
                <BacktrackrProvider>
                  {children}
                </BacktrackrProvider>
              ) : children}
            </SkinnrProvider>
          ) : enableHistory ? (
            <BacktrackrProvider>
              {children}
            </BacktrackrProvider>
          ) : children}
        </CommandrProvider>
      ) : enableNotifications ? (
        <NotifyrProvider options={notifyrOptions}>
          {enableTheming ? (
            <SkinnrProvider
              initialTheme={skinnrTheme}
              namespace={skinnrNamespace}
            >
              {enableHistory ? (
                <BacktrackrProvider>
                  {children}
                </BacktrackrProvider>
              ) : children}
            </SkinnrProvider>
          ) : enableHistory ? (
            <BacktrackrProvider>
              {children}
            </BacktrackrProvider>
          ) : children}
        </NotifyrProvider>
      ) : enableTheming ? (
        <SkinnrProvider
          initialTheme={skinnrTheme}
          namespace={skinnrNamespace}
        >
          {enableHistory ? (
            <BacktrackrProvider>
              {children}
            </BacktrackrProvider>
          ) : children}
        </SkinnrProvider>
      ) : enableHistory ? (
        <BacktrackrProvider>
          {children}
        </BacktrackrProvider>
      ) : children}
    </ActrProvider>
  );
}

export default CoreProvider;
